import { zonedAddDuration } from '@csp/csp-common-date-util';
import { Maybe, ZonedDateTime } from '@csp/csp-common-model';
import { RequestGroupMemberships } from '../request-group/model/RequestGroupMemberships';
import { ScheduleDateService } from '../service/ScheduleDateService';
import { TimingWindow } from '../timing/model/TimingWindow';
import { isWindowActiveNow } from '../util/scheduleUtil';
import { ScheduleIntervalService } from '../service/ScheduleIntervalService';
import { RequestWindowValidity } from './RequestWindowValidity';
import { ScheduleVersionRequestRef } from './ScheduleVersionRequestRef';
import { ScheduleWindowInterval } from './ScheduleWindowInterval';
import { RequestCode } from './codes/RequestCode';
import { ScheduleCode } from './codes/ScheduleCode';
import { VersionCode } from './codes/VersionCode';
import { GenericRequest } from './schedulingModels/GenericRequest';
import { ScheduleVersionInfo } from './schedulingModels/ScheduleVersionInfo';

export type RequestWindow = TimingWindow & {
  requestRefCodes: {
    requestCode: RequestCode;
    versionCode: VersionCode;
    scheduleCode: ScheduleCode;
  };
  groups?: RequestGroupMemberships;

  /**
   * Whether the request window interval is interrupted
   * by a while criteria that stopped matching user values mid-way.
   * When true the end user has not had full opportunity
   * to complete the task.
   */
  isInterrupted: boolean;

  /**
   * Can be used to determine which window to use when there are overlapping
   * windows caused by a status data change
   */
  validity: Maybe<RequestWindowValidity>;

  /**
   * To check if the zoned datetime is within the request window by comparing
   * date and time in a timezone-agnostic way.
   */
  isWithinWithoutTz(zonedDate: ZonedDateTime, missedThresholdInMinutes?: number): boolean;

  /**
   * The source id from where the start action was calculated.
   * Only used for request windows that are started based on APPOINTMENT_BOOKED startAction (will be populated with the visitId)
   */
  startActionSourceId?: string;
};

const toRequestWindow = (
  { requestCode, scheduleCode, versionCode }: ScheduleVersionRequestRef,
  timingWindow: TimingWindow,
  isInterrupted: boolean,
  groups: Maybe<RequestGroupMemberships>,
  validity?: RequestWindowValidity,
  startActionSourceId?: string,
): RequestWindow => ({
  requestRefCodes: {
    requestCode,
    versionCode,
    scheduleCode,
  },
  ...timingWindow,
  isInterrupted,
  validity,
  groups,
  startActionSourceId,
  isWithinWithoutTz: (zonedDate, thresholdInMinutes): boolean => {
    const utcDate = ScheduleDateService.replaceTimezoneNameInZonedDate(zonedDate, 'UTC');
    const windowStartUtc = ScheduleDateService.replaceTimezoneNameInZonedDate(timingWindow.windowStart, 'UTC');
    const windowEndUtc = ScheduleDateService.replaceTimezoneNameInZonedDateOrUndefined(timingWindow.windowEnd, 'UTC');
    const windowEndUtcWithGracePeriod =
      windowEndUtc && thresholdInMinutes
        ? zonedAddDuration(windowEndUtc, { minutes: thresholdInMinutes })
        : windowEndUtc;
    const intervalUtc = ScheduleWindowInterval.from(windowStartUtc, windowEndUtcWithGracePeriod);
    return isWindowActiveNow(intervalUtc, utcDate);
  },
});

const from = (
  request: GenericRequest,
  timingWindow: TimingWindow,
  isInterrupted: boolean,
  validity?: RequestWindowValidity,
  versionWindows?: ScheduleVersionInfo[],
  startActionSourceId?: string,
): RequestWindow =>
  toRequestWindow(
    {
      scheduleCode: request.scheduleCode,
      requestCode: request.requestCode,
      versionCode:
        versionWindows?.find(versionWindow =>
          ScheduleIntervalService.intersection(timingWindow, {
            windowStart: versionWindow.activeFrom,
            windowEnd: versionWindow.activeTo,
          }),
        )?.versionCode ?? request.versionCode,
    },
    timingWindow,
    isInterrupted,
    request.groups,
    validity,
    startActionSourceId,
  );

const fromRefAndTimingWindow = (
  requestRef: ScheduleVersionRequestRef,
  timingWindow: TimingWindow,
  isInterrupted = false,
): RequestWindow => toRequestWindow(requestRef, timingWindow, isInterrupted, undefined);

const toKey = (requestWindow: RequestWindow): string =>
  `${requestWindow.repeatIndex}-${requestWindow.windowReferenceTimestamp.unixTimeMillis}`;

export const RequestWindow = {
  from,
  fromRefAndTimingWindow,
  toKey,
  toRequestWindow,
};
